import { Grid, TextField, Typography } from '@mui/material'
import { CompanyType, VesselDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { LocationCodeAutoComplete, VesselServiceAutocomplete } from '@planning/components'
import { VesselAutoComplete } from '@planning/components/autocompletes/vessel-autocomplete/VesselAutoComplete'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { BerthPlanningViewStore } from '@planning/pages/BerthPlanning/Stores/BerthPlanningViewStore'
import { getVesselTypeLabel } from '@planning/pages/Vessel/components/Molecules/VesselForm'
import { VesselVisitGeneralInformationFormStore } from '@planning/pages/VesselVisit/Stores/VesselVisitGeneralInformationFormStore'
import companyService from '@planning/services/companyService'
import { VesselAutocompleteViewStore } from '@planning/stores/vessel/VesselAutocompleteViewStore'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import _ from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

type VesselVisitVesselSubFormProps = {
  store: VesselVisitGeneralInformationFormStore
  berthPlanningViewStore: BerthPlanningViewStore
  isEditMode: boolean
}

export const VesselVisitVesselSubForm = observer((props: VesselVisitVesselSubFormProps) => {
  const { store, berthPlanningViewStore, isEditMode } = props

  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { appViewStore, vesselServiceItemStore } = usePlanningStore()

  const vesselAutocompleteStore = useMemo(() => new VesselAutocompleteViewStore(), [])

  const getPortRotation = (id: number) => {
    const options = computed(() =>
      _.values(vesselServiceItemStore.elements).map(vesselService => vesselService.data),
    ).get()
    return options.find(item => item.id === id)?.portRotation
  }

  const getVesselCompanies = async (vessel: VesselDto) => {
    try {
      const [vesselOperator, shippingAgent] = await Promise.all([
        vessel.shippingLine
          ? companyService.getByShortName(vessel.shippingLine)
          : Promise.resolve(null),
        vessel.shippingAgentId
          ? companyService.getById(vessel.shippingAgentId)
          : Promise.resolve(null),
      ])

      if (vesselOperator) {
        store.vesselOperator.setValue(vesselOperator)
      }

      if (shippingAgent) {
        store.shippingAgent.setValue(shippingAgent)
      }
    } catch (error) {
      appViewStore.setShowAlert(
        'error',
        t(
          'failedToFetchVesselCompanies',
          'Failed to fetch "Vessel operator" and "Shipping Agent".',
        ),
      )
    }
  }

  const getVesselData = async (selectedVessel?: VesselDto | null): Promise<void> => {
    try {
      appViewStore.showLoader()
      if (!selectedVessel) {
        store.clearVesselData()
        return
      }

      store.vesselIds.setValue([selectedVessel.id])
      store.setSelectedVessel(selectedVessel)

      await getVesselCompanies(selectedVessel)

      if (selectedVessel.serviceId) {
        store.vesselServiceId.setValue(selectedVessel.serviceId)

        const portRotation = getPortRotation(selectedVessel.serviceId)
        store.portRotation.setValue(portRotation)
      }

      if (selectedVessel.remarks) {
        store.vesselNotes.setValue(selectedVessel.remarks)
      }
    } catch (error) {
      appViewStore.setShowAlert(
        'error',
        t('failedToFetchVesselData', 'Failed to fetch Vessel data'),
      )
    } finally {
      appViewStore.hideLoader()
    }
  }

  return (
    <>
      {store.visitNumber.value && (
        <Grid size={{ xs: 12 }}>
          <TextField
            {...store.visitNumber.props}
            fullWidth
            label={`${t('vesselVisitNo.', 'Vessel visit no.')}`}
            variant='outlined'
            type='text'
            name={`visitNumber`}
            disabled
          />
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <VesselAutoComplete
          id='vesselIds'
          sx={{ mb: 0 }}
          disabled={isEditMode}
          required
          validate={store.validate}
          store={vesselAutocompleteStore}
          label={t('vesselName', 'Vessel name')}
          defaultValue={store.selectedVessel}
          onChangeEntity={entity => {
            store.clearVesselData()
            getVesselData(entity)
            berthPlanningViewStore.calculateQmmBasedOnBerthAndVesselLength(
              berthPlanningViewStore.selectedBerths[0]?.quayMeterMarkFrom || 0,
            )
          }}
          onClear={() => store.clearVesselData()}
          error={store.vesselIds.isTouched && !!store.vesselIds.error}
          helperText={
            store.vesselIds.isTouched && !!store.vesselIds.error
              ? t('fieldIsRequired', 'Field is required.')
              : ''
          }
        />
      </Grid>

      {store.selectedVessel?.vesselType && (
        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('vesselType', 'Vessel type')}
            value={getVesselTypeLabel(store.selectedVessel.vesselType, t)}
            fullWidth
            disabled
            variant='outlined'
          />
        </Grid>
      )}

      {store.vesselIds.value.length ? (
        <>
          <Grid size={{ xs: 12 }}>
            <CompanyAutocomplete
              label={t('vesselOperator', 'Vessel operator')}
              required={true}
              validate={store.validate}
              selectedCustomer={store.vesselOperator.value}
              handleOnChange={customer => {
                store.vesselOperator.setValue(customer)
              }}
              error={!!store.vesselOperator.error}
              helperText={
                store.vesselOperator.error ? t('fieldIsRequired', 'Field is required.') : ''
              }
              filterByTypes={[CompanyType.VesselOperator]}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <CompanyAutocomplete
              label={t('shippingAgent', 'Shipping agent')}
              required={true}
              validate={store.validate}
              dataCy='vessel-visit-shipping-agent'
              selectedCustomer={store.shippingAgent.value}
              handleOnChange={customer => {
                store.shippingAgent.setValue(customer)
              }}
              error={!!store.shippingAgent.error}
              helperText={
                store.shippingAgent.error ? t('fieldIsRequired', 'Field is required.') : ''
              }
              filterByTypes={[CompanyType.ShippingAgent]}
            />
          </Grid>
        </>
      ) : null}

      <Grid size={{ xs: 12 }}>
        <TextField
          {...store.portCallId.props}
          fullWidth
          label={`${t('portCallId', 'Port call ID')}`}
          variant='outlined'
          type='text'
          name={`portCallId`}
          data-cy='vessel-visit-port-call-id-input'
        />
      </Grid>

      {store.vesselIds.value.length && store.vesselNotes.value ? (
        <Grid size={{ xs: 12 }}>
          <TextField
            {...store.vesselNotes.props}
            disabled
            multiline
            minRows={1}
            maxRows={7}
            fullWidth
            label={`${t('vesselNotes', 'Vessel notes')}`}
            variant='outlined'
            type='text'
            name={`vesselNotes`}
          />
        </Grid>
      ) : null}

      <Grid size={{ xs: 12 }} mt={theme.customSpacing.s}>
        <Typography variant='subtitle2'>{t('service', 'Service')}</Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <VesselServiceAutocomplete
          label={t('vesselService', 'Vessel service')}
          value={store.vesselServiceId.value}
          onChange={id => {
            store.vesselServiceId.setValue(id ?? null)
            store.portRotation.setValue(id ? getPortRotation(id) : [])
          }}
          hasError={store.vesselServiceId.isTouched && !!store.vesselServiceId.error}
          required
          validate={store.validate}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        {store.vesselServiceId.value ? (
          <LocationCodeAutoComplete
            defaultValue={store.portRotation.value ?? []}
            onChange={value => store.portRotation.setValue(value)}
            label={t('unCode', 'UN code')}
            error={store.portRotation.isTouched && !!store.portRotation.error}
            required
            validate={store.validate}
            multiple
            helperText={
              store.portRotation.isTouched && !!store.portRotation.error
                ? t('fieldIsRequired', 'Field is required')
                : undefined
            }
          />
        ) : null}
      </Grid>
    </>
  )
})
